import { useCallback } from "react";
import {
  Card,
  BlockStack,
  Text,
  TextField,
  FormLayout,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { PhoneNumberField } from "@shared/ui";
import { CityField } from "@shared/ui";
import { WebsiteField } from "@shared/ui";
import { Brand, GeoAddress } from "@features/events/types";
import { parsePhoneNumberWithError } from "libphonenumber-js";

interface ContactInformationCardProps {
  brandData: Partial<Brand>;
  onDataChange: (updates: Partial<Brand>) => void;
  fieldErrors: Record<string, string>;
}

export default function ContactInformationCard({
  brandData,
  onDataChange,
  fieldErrors,
}: ContactInformationCardProps) {
  const { t } = useTranslation();

  const handleEmailChange = useCallback(
    (value: string) => {
      onDataChange({ contactEmail: value });
    },
    [onDataChange]
  );

  const handleWebsiteChange = useCallback(
    (value: string) => {
      onDataChange({ website: value });
    },
    [onDataChange]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      try {
        const phoneObj = parsePhoneNumberWithError(value);
        if (phoneObj) {
          const cc = `+${phoneObj.countryCallingCode}`;
          const ln = phoneObj.nationalNumber;
          onDataChange({
            contactPhoneCountryCode: cc,
            contactPhone: ln,
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    [onDataChange]
  );

  const handleCityChange = useCallback(
    (city: GeoAddress | null) => {
      // Remove any bounds property if it exists
      if (city) {
        const { bounds: _bounds, ...cleanCity } = city as GeoAddress & {
          bounds?: unknown;
        };
        void _bounds;
        onDataChange({
          geoAddress: cleanCity as GeoAddress,
        });
      } else {
        onDataChange({
          geoAddress: undefined,
        });
      }
    },
    [onDataChange]
  );

  // Combine phone country code and phone number for PhoneNumberField
  const fullPhoneNumber = `${brandData.contactPhoneCountryCode || "+359"}${
    brandData.contactPhone || ""
  }`;

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("brandProfile.contact.title")}
        </Text>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("brandProfile.contact.subtitle")}
        </Text>

        <FormLayout>
          <CityField
            label={t("brandProfile.contact.city")}
            value={brandData.geoAddress || null}
            onChange={handleCityChange}
            error={fieldErrors.geoAddress}
            requiredIndicator
          />

          <PhoneNumberField
            phoneLabel={t("brandProfile.contact.phone")}
            value={fullPhoneNumber}
            onChange={handlePhoneChange}
            phoneError={fieldErrors.contactPhone || fieldErrors.contactPhoneCountryCode}
          />

          <TextField
            label={t("brandProfile.contact.email")}
            type="email"
            value={brandData.contactEmail || ""}
            onChange={handleEmailChange}
            error={fieldErrors.contactEmail}
            autoComplete="email"
            requiredIndicator
          />

          <WebsiteField
            label={t("brandProfile.contact.website")}
            value={brandData.website || ""}
            onChange={handleWebsiteChange}
            error={fieldErrors.website}
            placeholder={t("common.placeholders.url")}
            requiredIndicator
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
